import {
  isApiErrorPayload,
  isHousehold,
  type BulkHouseholdsResponse,
  type CreateCodeRequest,
  type CreateDeviceRequest,
  type CreateHouseholdRequest,
  type DevicesResponse,
  type EtaResponse,
  type FleetResponse,
  type GeneratedCode,
  type Household,
  type HouseholdCreated,
  type HouseholdPatch,
  type HouseholdsQuery,
  type HouseholdsResponse,
  type IssuedDevice,
  type LiveResponse,
  type NotificationsQuery,
  type NotificationsResponse,
  type OperatorStats,
  type PQRSItem,
  type PQRSListResponse,
  type PQRSQuery,
  type PQRSStatus,
  type ReadyResponse,
  type Route,
  type RouteRequest,
  type Vehicle,
  type VehiclePatch,
  type VehicleRequest,
} from "./types";
import { normalizeBaseUrl } from "./config";

const DEFAULT_TIMEOUT_MS = 8000;

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

export function isAbortError(error: unknown): boolean {
  if (error instanceof DOMException) return error.name === "AbortError";
  return error instanceof Error && error.name === "AbortError";
}

export function describeError(error: unknown): string {
  if (error instanceof ApiError) {
    const tag = error.status > 0 ? `${error.status} ${error.code}` : error.code;
    return `${error.message} [${tag}]`;
  }
  if (error instanceof Error) return error.message;
  return String(error);
}

export interface ApiClientOptions {
  baseUrl: string;
  apiKey: string;
  requestTimeoutMs?: number;
}

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal | undefined;
  auth?: boolean;
}

/** Resultado de /readyz: listo (200) o dependencia caída (503). La red se reporta como excepción. */
export type ReadyResult =
  | { state: "ready"; store: string; cache: string }
  | { state: "unavailable"; message: string };

interface CallOptions {
  signal?: AbortSignal;
}

function toApiError(response: Response): Promise<ApiError> {
  const fallbackMessage = `HTTP ${response.status} ${response.statusText}`.trim();
  return response
    .json()
    .then((payload: unknown) => {
      if (isApiErrorPayload(payload)) {
        return new ApiError(response.status, payload.error.code, payload.error.message || fallbackMessage);
      }
      return new ApiError(response.status, "http_error", fallbackMessage);
    })
    .catch(() => new ApiError(response.status, "http_error", fallbackMessage));
}

function normalizeCreatedHousehold(created: HouseholdCreated): Household {
  return isHousehold(created) ? created : created.household;
}

export class ApiClient {
  readonly baseUrl: string;
  private readonly apiKey: string;
  private readonly requestTimeoutMs: number;

  constructor(options: ApiClientOptions) {
    this.baseUrl = normalizeBaseUrl(options.baseUrl);
    this.apiKey = options.apiKey;
    this.requestTimeoutMs = options.requestTimeoutMs ?? DEFAULT_TIMEOUT_MS;
  }

  fleet(options: CallOptions = {}): Promise<FleetResponse> {
    return this.request<FleetResponse>("/v1/operator/fleet", { method: "GET", signal: options.signal });
  }

  live(options: CallOptions = {}): Promise<LiveResponse> {
    return this.request<LiveResponse>("/v1/operator/live", { method: "GET", signal: options.signal });
  }

  notifications(params: NotificationsQuery = {}, options: CallOptions = {}): Promise<NotificationsResponse> {
    const search = new URLSearchParams();
    if (params.limit !== undefined) {
      const safeLimit = Math.min(Math.max(Math.trunc(params.limit), 1), 200);
      search.set("limit", String(safeLimit));
    }
    if (params.offset !== undefined && params.offset > 0) search.set("offset", String(Math.trunc(params.offset)));
    if (params.householdId !== undefined && params.householdId !== "") {
      search.set("household_id", params.householdId);
    }
    if (params.since !== undefined && params.since !== "") search.set("since", params.since);
    const query = search.size > 0 ? `?${search.toString()}` : "";
    return this.request<NotificationsResponse>(`/v1/operator/notifications${query}`, {
      method: "GET",
      signal: options.signal,
    });
  }

  stats(options: CallOptions = {}): Promise<OperatorStats> {
    return this.request<OperatorStats>("/v1/operator/stats", { method: "GET", signal: options.signal });
  }

  ready(options: CallOptions = {}): Promise<ReadyResult> {
    return this.request<ReadyResponse>("/readyz", { method: "GET", auth: false, signal: options.signal })
      .then(
        (data): ReadyResult => ({ state: "ready", store: data.store, cache: data.cache }),
      )
      .catch((error: unknown): ReadyResult => {
        if (error instanceof ApiError && error.status === 503) {
          return { state: "unavailable", message: error.message };
        }
        throw error;
      });
  }

  deleteDemoData(options: CallOptions = {}): Promise<void> {
    return this.request<void>("/v1/operator/demo-data", { method: "DELETE", signal: options.signal });
  }

  devices(options: CallOptions = {}): Promise<DevicesResponse> {
    return this.request<DevicesResponse>("/v1/operator/devices", { method: "GET", signal: options.signal });
  }

  bulkHouseholds(households: CreateHouseholdRequest[], options: CallOptions = {}): Promise<BulkHouseholdsResponse> {
    return this.request<BulkHouseholdsResponse>("/v1/operator/households/bulk", {
      method: "POST",
      body: { households },
      signal: options.signal,
    });
  }

  createVehicle(payload: VehicleRequest, options: CallOptions = {}): Promise<Vehicle> {
    return this.request<Vehicle>("/v1/operator/vehicles", { method: "POST", body: payload, signal: options.signal });
  }

  updateVehicle(id: string, patch: VehiclePatch, options: CallOptions = {}): Promise<Vehicle> {
    return this.request<Vehicle>(`/v1/operator/vehicles/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: patch,
      signal: options.signal,
    });
  }

  createRoute(payload: RouteRequest, options: CallOptions = {}): Promise<Route> {
    return this.request<Route>("/v1/operator/routes", { method: "POST", body: payload, signal: options.signal });
  }

  updateRoute(id: string, patch: RouteRequest, options: CallOptions = {}): Promise<Route> {
    return this.request<Route>(`/v1/operator/routes/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: patch,
      signal: options.signal,
    });
  }

  pqrs(params: PQRSQuery = {}, options: CallOptions = {}): Promise<PQRSListResponse> {
    const search = new URLSearchParams();
    if (params.limit !== undefined) search.set("limit", String(params.limit));
    if (params.offset !== undefined && params.offset > 0) search.set("offset", String(params.offset));
    if (params.status !== undefined && params.status !== "") search.set("status", params.status);
    const suffix = search.size > 0 ? `?${search.toString()}` : "";
    return this.request<PQRSListResponse>(`/v1/operator/pqrs${suffix}`, {
      method: "GET",
      signal: options.signal,
    });
  }

  updatePQRS(id: string, status: PQRSStatus, options: CallOptions = {}): Promise<PQRSItem> {
    return this.request<PQRSItem>(`/v1/operator/pqrs/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: { status },
      signal: options.signal,
    });
  }

  createDevice(payload: CreateDeviceRequest, options: CallOptions = {}): Promise<IssuedDevice> {
    return this.request<IssuedDevice>("/v1/operator/devices", {
      method: "POST",
      body: payload,
      signal: options.signal,
    });
  }

  revokeDevice(id: string, options: CallOptions = {}): Promise<void> {
    return this.request<void>(`/v1/operator/devices/${encodeURIComponent(id)}`, {
      method: "DELETE",
      signal: options.signal,
    });
  }

  createCode(payload: CreateCodeRequest, options: CallOptions = {}): Promise<GeneratedCode> {
    return this.request<GeneratedCode>("/v1/operator/codes", { method: "POST", body: payload, signal: options.signal });
  }

  households(params: HouseholdsQuery = {}, options: CallOptions = {}): Promise<HouseholdsResponse> {
    const search = new URLSearchParams();
    const query = params.q?.trim() ?? "";
    if (query !== "") search.set("q", query);
    if (params.limit !== undefined) {
      const safeLimit = Math.min(Math.max(Math.trunc(params.limit), 1), 200);
      search.set("limit", String(safeLimit));
    }
    if (params.offset !== undefined && params.offset > 0) search.set("offset", String(Math.trunc(params.offset)));
    const suffix = search.size > 0 ? `?${search.toString()}` : "";
    // El padrón exige API key de operador (dato personal: no es público).
    return this.request<HouseholdsResponse>(`/v1/households${suffix}`, {
      method: "GET",
      signal: options.signal,
    });
  }

  createHousehold(payload: CreateHouseholdRequest, options: CallOptions = {}): Promise<Household> {
    return this.request<HouseholdCreated>("/v1/households", {
      method: "POST",
      body: payload,
      auth: false,
      signal: options.signal,
    }).then(normalizeCreatedHousehold);
  }

  updateHousehold(id: string, patch: HouseholdPatch, options: CallOptions = {}): Promise<Household> {
    return this.request<Household>(`/v1/households/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: patch,
      auth: false,
      signal: options.signal,
    });
  }

  deleteHousehold(id: string, options: CallOptions = {}): Promise<void> {
    return this.request<void>(`/v1/households/${encodeURIComponent(id)}`, {
      method: "DELETE",
      auth: false,
      signal: options.signal,
    });
  }

  householdEta(householdId: string, options: CallOptions = {}): Promise<EtaResponse> {
    return this.request<EtaResponse>(`/v1/households/${encodeURIComponent(householdId)}/eta`, {
      method: "GET",
      auth: false,
      signal: options.signal,
    });
  }

  private async request<T>(path: string, options: RequestOptions): Promise<T> {
    const url = `${this.baseUrl}${path}`;
    const controller = new AbortController();
    let timedOut = false;
    const timeoutId = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, this.requestTimeoutMs);

    const externalSignal = options.signal;
    const onAbort = (): void => controller.abort();
    if (externalSignal !== undefined) {
      if (externalSignal.aborted) controller.abort();
      else externalSignal.addEventListener("abort", onAbort, { once: true });
    }

    const headers: Record<string, string> = { Accept: "application/json" };
    if (options.auth ?? true) headers["X-API-Key"] = this.apiKey;

    let body: string | undefined;
    if (options.body !== undefined) {
      headers["Content-Type"] = "application/json";
      body = JSON.stringify(options.body);
    }

    try {
      const response = await fetch(url, {
        method: options.method ?? "GET",
        headers,
        body,
        signal: controller.signal,
      });

      if (!response.ok) throw await toApiError(response);
      if (response.status === 204) return undefined as unknown as T;

      try {
        return (await response.json()) as T;
      } catch (error) {
        if (isAbortError(error)) throw error;
        throw new ApiError(response.status, "invalid_response", "No pudimos interpretar la respuesta del servicio.");
      }
    } catch (error) {
      if (error instanceof ApiError) throw error;
      if (isAbortError(error)) {
        if (timedOut && !(externalSignal?.aborted ?? false)) {
          throw new ApiError(0, "timeout", "El servicio tardó demasiado. Intenta de nuevo.");
        }
        throw error;
      }
      throw new ApiError(0, "network", "No pudimos conectar. Revisa tu conexión a internet.");
    } finally {
      window.clearTimeout(timeoutId);
      externalSignal?.removeEventListener("abort", onAbort);
    }
  }
}
